// src/theme.ts
import { ThemeOptions } from '@mui/material/styles';

const light = {
  primary: { main: '#DB0058', hover: '#B8004A', light: 'rgba(219, 0, 88, 0.08)' },
  secondary: { main: '#075985', hover: '#0369A1', light: 'rgba(7, 89, 133, 0.08)' },
  accent: { main: '#6D28D9', hover: '#5B21B6', light: 'rgba(109, 40, 217, 0.08)' },
  background: {
    default: '#F8F9FA',
    paper: '#FFFFFF',
    input: '#E9ECEF',
    cardHover: '#F1F3F5',
    tooltip: '#212529',
  },
  text: { primary: '#0F172A', secondary: '#334155', muted: '#64748B', disabled: '#94A3B8' },
  border: { main: '#DEE2E6', subtle: '#F1F3F5', strong: '#ADB5BD' },
  divider: '#DEE2E6',
  success: { main: '#059669', hover: '#047857', light: 'rgba(5, 150, 105, 0.08)' },
  warning: { main: '#D97706', light: 'rgba(217, 119, 6, 0.08)' },
  error: { main: '#DC2626', hover: '#B91C1C', light: 'rgba(220, 38, 38, 0.08)' },
  info: { main: '#2563EB', light: 'rgba(37, 99, 235, 0.08)' },
  packetLoss: '#7C3AED',
  shadow: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.07), 0 2px 4px -1px rgba(0, 0, 0, 0.04)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  },
};

const dark = {
  primary: { main: '#DB0058', hover: '#FF3399', light: 'rgba(255, 0, 122, 0.1)' },
  secondary: { main: '#00D1FF', hover: '#33DDFF', light: 'rgba(0, 209, 255, 0.1)' },
  accent: { main: '#00D1FF', hover: '#33DDFF', light: 'rgba(0, 209, 255, 0.1)' },
  background: {
    default: '#050A18',
    paper: '#0D1528',
    input: '#030712',
    cardHover: '#141E36',
    tooltip: '#0F172A',
  },
  text: { primary: '#F9FAFB', secondary: '#D1D5DB', muted: '#8E97A6', disabled: '#4B5563' },
  border: { main: 'rgba(255, 255, 255, 0.05)', subtle: 'rgba(255, 255, 255, 0.02)', strong: 'rgba(255, 255, 255, 0.12)' },
  divider: 'rgba(255, 255, 255, 0.05)',
  success: { main: '#10B981', hover: '#34D399', light: 'rgba(16, 185, 129, 0.1)' },
  warning: { main: '#F59E0B', light: 'rgba(245, 158, 11, 0.1)' },
  error: { main: '#EF4444', hover: '#F87171', light: 'rgba(239, 68, 68, 0.1)' },
  info: { main: '#3B82F6', light: 'rgba(59, 130, 246, 0.1)' },
  packetLoss: '#00D1FF',
  shadow: {
    sm: 'none',
    md: 'none',
    lg: '0 18px 48px rgba(0, 0, 0, 0.35)',
  },
};

type PaletteTokens = typeof light | typeof dark;

const hexToRgb = (hex: string) => {
  const clean = hex.replace('#', '');
  const normalized = clean.length === 3
    ? clean.split('').map((char) => `${char}${char}`).join('')
    : clean;
  const value = Number.parseInt(normalized, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `${r}, ${g}, ${b}`;
};

const buildCssVars = (p: PaletteTokens, mode: 'light' | 'dark') => `
  :root, [data-theme="${mode}"] {
    --primary: ${p.primary.main};
    --primary-rgb: ${hexToRgb(p.primary.main)};
    --on-primary: #FFFFFF;
    --on-primary-rgb: 255, 255, 255;
    --primary-hover: ${p.primary.hover};
    --primary-light: ${p.primary.light};
    --secondary: ${p.secondary.main};
    --secondary-rgb: ${hexToRgb(p.secondary.main)};
    --on-secondary: ${mode === 'dark' ? '#061018' : '#FFFFFF'};
    --on-secondary-rgb: ${mode === 'dark' ? '6, 16, 24' : '255, 255, 255'};
    --secondary-hover: ${p.secondary.hover};
    --secondary-light: ${p.secondary.light};
    --accent: ${p.accent.main};
    --accent-rgb: ${hexToRgb(p.accent.main)};
    --on-accent: ${mode === 'dark' ? '#061018' : '#FFFFFF'};
    --on-accent-rgb: ${mode === 'dark' ? '6, 16, 24' : '255, 255, 255'};
    --accent-hover: ${p.accent.hover};
    --accent-light: ${p.accent.light};
    --bg-body: ${p.background.default};
    --bg-body-rgb: ${hexToRgb(p.background.default)};
    --bg-card: ${p.background.paper};
    --bg-card-rgb: ${hexToRgb(p.background.paper)};
    --bg-card-hover: ${p.background.cardHover};
    --bg-input: ${p.background.input};
    --bg-tooltip: ${p.background.tooltip};
    --border: ${p.border.main};
    --border-subtle: ${p.border.subtle};
    --border-strong: ${p.border.strong};
    --text-main: ${p.text.primary};
    --text-main-rgb: ${hexToRgb(p.text.primary)};
    --text-secondary: ${p.text.secondary};
    --text-secondary-rgb: ${hexToRgb(p.text.secondary)};
    --text-muted: ${p.text.muted};
    --text-disabled: ${p.text.disabled};
    --danger: ${p.error.main};
    --danger-rgb: ${hexToRgb(p.error.main)};
    --on-danger: #FFFFFF;
    --on-danger-rgb: 255, 255, 255;
    --danger-hover: ${p.error.hover};
    --danger-light: ${p.error.light};
    --success: ${p.success.main};
    --success-rgb: ${hexToRgb(p.success.main)};
    --on-success: #FFFFFF;
    --on-success-rgb: 255, 255, 255;
    --success-hover: ${p.success.hover};
    --success-light: ${p.success.light};
    --warning: ${p.warning.main};
    --warning-rgb: ${hexToRgb(p.warning.main)};
    --on-warning: #111827;
    --on-warning-rgb: 17, 24, 39;
    --warning-light: ${p.warning.light};
    --info: ${p.info.main};
    --info-rgb: ${hexToRgb(p.info.main)};
    --info-light: ${p.info.light};
    --packet-loss-color: ${p.packetLoss};
    --shadow-sm: ${p.shadow.sm};
    --shadow-md: ${p.shadow.md};
    --shadow-lg: ${p.shadow.lg};
  }
`;

export const getTheme = (mode: 'light' | 'dark'): ThemeOptions => {
  const p = mode === 'light' ? light : dark;
  const radius = 4;
  const selectedBg = mode === 'light' ? p.primary.light : p.secondary.light;
  const hoverBg = mode === 'light' ? p.background.cardHover : p.background.cardHover;
  const panelBg = p.background.paper;
  const headingColor = p.text.primary;

  return {
    palette: {
      mode,
      primary: { main: p.primary.main, contrastText: '#FFFFFF' },
      secondary: { main: p.secondary.main, contrastText: '#FFFFFF' },
      success: { main: p.success.main },
      warning: { main: p.warning.main },
      error: { main: p.error.main },
      info: { main: p.info.main },
      background: { default: p.background.default, paper: p.background.paper },
      text: { primary: p.text.primary, secondary: p.text.secondary, disabled: p.text.disabled },
      divider: p.divider,
    },
    shape: { borderRadius: radius },
    typography: {
      fontFamily: '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
      fontSize: 11,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.04em', color: headingColor },
      h2: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.18, letterSpacing: '-0.035em', color: headingColor },
      h3: { fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.22, letterSpacing: '-0.03em', color: headingColor },
      h4: { fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.28, letterSpacing: '-0.02em', color: headingColor },
      h5: { fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.35, color: headingColor },
      h6: { fontSize: '0.82rem', fontWeight: 700, lineHeight: 1.4, color: headingColor },
      subtitle1: { fontSize: '0.84rem', fontWeight: 600, lineHeight: 1.5 },
      subtitle2: { fontSize: '0.78rem', fontWeight: 600, lineHeight: 1.45 },
      body1: { fontSize: '0.8rem', fontWeight: 400, lineHeight: 1.5 },
      body2: { fontSize: '0.74rem', fontWeight: 400, lineHeight: 1.45 },
      caption: { fontSize: '0.66rem', fontWeight: 500, lineHeight: 1.35, color: p.text.muted },
      overline: { fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' as const, color: p.text.disabled },
      button: { fontSize: '0.74rem', fontWeight: 600, textTransform: 'none' as const, letterSpacing: '0.02em' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: buildCssVars(p, mode) + `
          html, body, #root { min-height: 100%; }
          body { background: ${p.background.default}; color: ${p.text.primary}; }
          * { scrollbar-width: thin; scrollbar-color: ${p.border.strong} transparent; }
          *::-webkit-scrollbar { width: 4px; height: 4px; }
          *::-webkit-scrollbar-track { background: transparent; }
          *::-webkit-scrollbar-thumb { background: ${p.border.strong}; border-radius: ${radius}px; }
          *::-webkit-scrollbar-thumb:hover { background: ${mode === 'light' ? '#94A3B8' : 'rgba(255,255,255,0.18)'}; }
        `,
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: panelBg,
            color: p.text.primary,
            borderBottom: `1px solid ${p.border.main}`,
            boxShadow: 'none',
            borderRadius: 0,
            backgroundImage: 'none',
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: '48px !important',
            color: 'inherit',
            '& .MuiButton-root, & .MuiIconButton-root': { color: 'inherit' },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundColor: panelBg, backgroundImage: 'none', boxShadow: p.shadow.sm, borderRadius: radius },
          outlined: { border: `1px solid ${p.border.main}`, boxShadow: 'none' },
          elevation1: { boxShadow: p.shadow.sm },
          elevation2: { boxShadow: p.shadow.md },
          elevation3: { boxShadow: p.shadow.lg },
          elevation4: { boxShadow: p.shadow.lg },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: panelBg,
            borderRadius: 6,
            border: `1px solid ${p.border.main}`,
            boxShadow: p.shadow.sm,
            backgroundImage: 'none',
            overflow: 'hidden',
            transition: 'border-color 0.15s, background-color 0.15s',
            '&:hover': { borderColor: p.border.strong },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: radius,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
            transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
            '&:hover, &:active': { boxShadow: 'none' },
            '&:focus-visible': { outline: `2px solid ${p.accent.main}`, outlineOffset: 2 },
          },
          containedPrimary: { backgroundColor: p.primary.main, color: '#FFFFFF', '&:hover': { backgroundColor: p.primary.hover } },
          containedSecondary: { backgroundColor: p.secondary.main, color: mode === 'dark' ? '#061018' : '#FFFFFF', '&:hover': { backgroundColor: p.secondary.hover } },
          containedError: { backgroundColor: p.error.main, color: '#FFFFFF', '&:hover': { backgroundColor: p.error.hover } },
          outlined: { borderWidth: 1, borderColor: p.border.strong, '&:hover': { borderWidth: 1, backgroundColor: hoverBg, borderColor: p.accent.main } },
          text: { '&:hover': { backgroundColor: hoverBg } },
          sizeSmall: { fontSize: '0.68rem', padding: '4px 9px' },
          sizeMedium: { fontSize: '0.74rem', padding: '5px 12px' },
          sizeLarge: { fontSize: '0.82rem', padding: '8px 16px' },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { borderRadius: radius, transition: 'background-color 0.15s, color 0.15s', '&:hover': { backgroundColor: hoverBg } },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius,
            fontSize: '0.78rem',
            backgroundColor: p.background.input,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: p.border.main, borderWidth: 1 },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: p.border.strong, borderWidth: 1 },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: p.accent.main, borderWidth: 1 },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: p.error.main },
          },
          input: { padding: '7px 11px', fontSize: '0.76rem' },
          inputSizeSmall: { padding: '5px 9px' },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { fontSize: '0.76rem', color: p.text.muted, '&.Mui-focused': { color: p.accent.main } },
          sizeSmall: { fontSize: '0.72rem' },
        },
      },
      MuiFormHelperText: { styleOverrides: { root: { fontSize: '0.68rem', marginTop: 3, color: p.text.muted } } },
      MuiSelect: { styleOverrides: { root: { borderRadius: radius, fontSize: '0.78rem' }, select: { padding: '8px 12px' } } },
      MuiMenuItem: {
        styleOverrides: {
          root: { fontSize: '0.78rem', minHeight: 34, '&:hover': { backgroundColor: hoverBg }, '&.Mui-selected': { backgroundColor: selectedBg, color: p.accent.main }, '&.Mui-selected:hover': { backgroundColor: selectedBg } },
        },
      },
      MuiTabs: { styleOverrides: { root: { minHeight: 40, borderBottom: `1px solid ${p.border.main}` }, indicator: { backgroundColor: p.accent.main, height: 2 } } },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', minHeight: 40, padding: '0 16px', color: p.text.disabled, letterSpacing: '0.03em', '&:hover': { color: p.text.secondary }, '&.Mui-selected': { color: p.accent.main, fontWeight: 700 } },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { fontSize: '0.78rem', padding: '13px 20px', borderBottom: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.035)' : p.border.main}`, color: p.text.secondary },
          head: { fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.18em', color: p.text.disabled, backgroundColor: p.background.default, padding: '10px 20px' },
        },
      },
      MuiTableRow: { styleOverrides: { root: { '&:last-child .MuiTableCell-root': { borderBottom: 'none' }, '&:hover .MuiTableCell-root': { backgroundColor: hoverBg, color: p.text.primary } } } },
      MuiChip: { styleOverrides: { root: { borderRadius: 3, fontSize: '0.66rem', fontWeight: 700, height: 22, letterSpacing: '0.04em' }, label: { padding: '0 8px' } } },
      MuiSwitch: {
        styleOverrides: {
          root: { padding: 6 },
          thumb: { borderRadius: radius, width: 12, height: 12 },
          track: { borderRadius: radius },
          switchBase: { padding: 8, '&.Mui-checked + .MuiSwitch-track': { backgroundColor: p.accent.main, opacity: 1 } },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 6, boxShadow: p.shadow.lg, border: `1px solid ${p.border.main}`, backgroundImage: 'none' },
        },
      },
      MuiDialogTitle: { styleOverrides: { root: { fontSize: '0.9rem', fontWeight: 700, padding: '16px 20px 12px', borderBottom: `1px solid ${p.border.main}` } } },
      MuiDialogContent: { styleOverrides: { root: { padding: '16px 20px' } } },
      MuiDialogActions: { styleOverrides: { root: { padding: '12px 20px', borderTop: `1px solid ${p.border.main}`, gap: 8 } } },
      MuiDrawer: { styleOverrides: { paper: { borderRadius: 0, border: 'none', borderRight: `1px solid ${p.border.main}`, backgroundImage: 'none', backgroundColor: p.background.default } } },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: radius,
            fontSize: '0.84rem',
            color: p.text.muted,
            border: '1px solid transparent',
            '&:hover': { backgroundColor: hoverBg, color: p.text.primary, borderColor: p.border.main },
            '&.Mui-selected': { backgroundColor: selectedBg, color: p.accent.main, borderColor: mode === 'dark' ? 'rgba(0,209,255,0.27)' : 'rgba(124,58,237,0.27)', '& .MuiListItemIcon-root': { color: p.accent.main }, '&:hover': { backgroundColor: selectedBg } },
          },
        },
      },
      MuiListItemIcon: { styleOverrides: { root: { minWidth: 36, color: p.text.muted } } },
      MuiListItemText: { styleOverrides: { primary: { fontSize: '0.84rem', fontWeight: 500 }, secondary: { fontSize: '0.72rem', color: p.text.disabled } } },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: radius, fontSize: '0.76rem', padding: '8px 14px', border: `1px solid ${p.border.main}` },
          standardInfo: { backgroundColor: p.info.light, color: mode === 'light' ? '#1D4ED8' : '#93C5FD' },
          standardError: { backgroundColor: p.error.light, color: mode === 'light' ? '#B91C1C' : '#FCA5A5' },
          standardSuccess: { backgroundColor: p.success.light, color: mode === 'light' ? '#047857' : '#6EE7B7' },
          standardWarning: { backgroundColor: p.warning.light, color: mode === 'light' ? '#92400E' : '#FCD34D' },
        },
      },
      MuiLinearProgress: { styleOverrides: { root: { borderRadius: 2, height: 4, backgroundColor: p.border.main }, bar: { backgroundColor: p.accent.main } } },
      MuiDivider: { styleOverrides: { root: { borderColor: p.border.main } } },
      MuiTooltip: { styleOverrides: { tooltip: { borderRadius: radius, fontSize: '0.68rem', fontWeight: 500, backgroundColor: p.background.tooltip, color: '#F9FAFB', padding: '5px 10px' }, arrow: { color: p.background.tooltip } } },
      MuiAccordion: { styleOverrides: { root: { borderRadius: '6px !important', boxShadow: 'none', border: `1px solid ${p.border.main}`, backgroundColor: panelBg, '&:before': { display: 'none' } } } },
      MuiBackdrop: { styleOverrides: { root: { backgroundColor: mode === 'light' ? 'rgba(15,23,42,0.45)' : 'rgba(0,0,0,0.65)' } } },
    },
  };
};
